import { isClosedStatus, isPastStay, signUpMessage } from '@bbbk/schema';
import { moneyMessage, moneyState } from '../bookings/MoneyCard';
import { dates, dogNames } from '../bookings/MessageSlot';
import type { BookingDetail } from '../bookings/types';

// APP ACCESS SAYS THE NEXT STEP (Freddi, 2026-10-14: "encourage with the registered link message
// to proceed to the next step, so whatever the booking needs like if we have their booking but it
// needs a deposit"). The message beside a person's link is the household's next step, in the one
// shape, with their link in it: the deposit if the stay is waiting on it; else the stay and the
// link. A screenshot with Patricia is not the customer's next step.
//
// NEVER THE VACCINATIONS (Freddi, 2026-10-15: "remove any mention of vaccinations due from it … it
// should be kept only in the vaccination card and not spill into the generic messaging in the self
// service card for unregistered/registered users"). A card the customer owes is the Vaccinations
// card's own message to send; App access no longer borrows it.

export function accessMessage(args: {
  first: string;
  /** The person's link: their sign-up link, or the app itself once registered. */
  url: string;
  registered: boolean;
  /** The stay open beside App access, with its money, or null on the household pane. */
  booking: BookingDetail | null;
  /**
   * The household's next stay (`accessNextStay`), read in full — its money too — for when no stay is
   * open or the open one is over. Until it has loaded, `stay` names it from its row.
   */
  next?: BookingDetail | null;
  /** The household's next stay as its row reads, when no stay is open. */
  stay?: { checkIn: string; checkOut: string; dogs: string } | null;
  today: string;
}): string {
  const link = args.registered ? null : args.url;
  // A STAY THAT IS OVER IS NOT A BOOKING (Freddi, 2026-10-15: "this is the message when theres no future
  // bookings … should be changed to not mention any booking but encourage them to sign up"). Collected,
  // or its last day gone: set aside, so the household's next stay is named if it has one, else the
  // sign-up alone — and no deposit is asked of a stay already over.
  if (args.booking && (args.booking.status === 'collected' || isPastStay({ checkOut: args.booking.checkOut }, args.today))) {
    return accessMessage({ ...args, booking: null });
  }
  // THE HOUSEHOLD PANE SAYS WHAT THE STAY PANE SAYS (audit, 2026-10-15): with no stay open, the next one
  // speaks as it would open — its deposit asked for, not only "booked in".
  if (!args.booking && args.next) return accessMessage({ ...args, booking: args.next, next: null, stay: null });
  // A STAY THAT CANNOT NEED ANYTHING YET IS NOT A STAY TO SPEAK OF (PR #267 review): a request not yet
  // answered, a decline, a cancellation — the Money card greys these, so App access asks for no deposit
  // and says nothing is booked in; the sign-up alone.
  if (args.booking && ['requested', 'declined', 'cancelled'].includes(args.booking.status)) {
    return signUpMessage({ first: args.first, link: args.url, registered: args.registered, stay: null });
  }
  if (args.booking) {
    const s = moneyState(args.booking);
    const owed = !s.settled && s.lead.kind === 'deposit' && (s.lead.state === 'due' || s.lead.state === 'refused');
    if (owed) return moneyMessage(s, args.booking, args.first, link);
  }
  const b = args.booking;
  const stay = b
    ? { dogs: dogNames(b), are: ((b.dogs ?? []).length === 1 ? 'is' : 'are') as 'is' | 'are', dates: dates(b) }
    : args.stay
      ? { dogs: args.stay.dogs.split(',').map((d) => d.trim()).filter(Boolean).join(' & ') || 'the dogs', are: (args.stay.dogs.includes(',') ? 'are' : 'is') as 'is' | 'are', dates: dates(args.stay) }
      : null;
  return signUpMessage({ first: args.first, link: args.url, registered: args.registered, stay });
}

/**
 * THE HOUSEHOLD'S NEXT STAY, FOR APP ACCESS (audit, 2026-10-15): the first still to come that is booked —
 * never a request not yet answered (it is not "booked in"), a decline, a cancellation, or a stay over. A
 * stay beginning today has begun (PR #275 review), as "See you on" counts it.
 */
export function accessNextStay<T extends { checkIn: string; status: string }>(rows: readonly T[], today: string): T | null {
  return [...rows]
    .filter((b) => b.checkIn > today && !isClosedStatus(b.status as never) && b.status !== 'requested' && b.status !== 'collected')
    .sort((a, b) => a.checkIn.localeCompare(b.checkIn))[0] ?? null;
}
